import React, { useState, useEffect } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { 
  getTeamMembers, 
  inviteUserToTeam, 
  getTeamInvitations,
  updateTeamMemberRole,
  removeTeamMember,
  supabase,
  getCurrentSession
} from '../../lib/supabase'
import { TeamRole } from '../../types/supabase'
import { BillingManagement } from '../BillingManagement'
import { StandupConfiguration } from '../StandupConfiguration'
import { canAddTeamMember, getPlanFeatures } from '../../lib/billing'
import { Crown, AlertTriangle, Slack } from 'lucide-react'

interface TeamSettingsProps {
  teamId: string
  userRole: TeamRole
}

export const TeamSettings: React.FC<TeamSettingsProps> = ({ teamId, userRole }) => {
  const { refreshUserTeams } = useAuth()
  const [members, setMembers] = useState<any[]>([])
  const [invitations, setInvitations] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [activeTab, setActiveTab] = useState<'members' | 'billing' | 'standup'>('members')
  const [teamPlan, setTeamPlan] = useState<string>('trial')
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false)
  
  // Invite form state
  const [showInviteForm, setShowInviteForm] = useState(false)
  const [inviteLoading, setInviteLoading] = useState(false)
  const [slackUsers, setSlackUsers] = useState<any[]>([])
  const [loadingSlackUsers, setLoadingSlackUsers] = useState(false)
  const [selectedSlackUsers, setSelectedSlackUsers] = useState<Set<string>>(new Set())

  const isAdmin = userRole === 'admin'

  useEffect(() => {
    loadTeamData()
  }, [teamId])

  const loadSlackUsers = async () => {
    try {
      setLoadingSlackUsers(true)
      const session = await getCurrentSession()
      if (!session) throw new Error('No session found')

      const { data, error } = await supabase.functions.invoke('get-slack-users', {
        body: { team_id: teamId },
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      })

      if (error) throw error

      // Filter out users who are already team members
      const currentMemberEmails = members.map(m => m.users?.email).filter(Boolean)
      const availableUsers = data?.users?.filter((user: any) => 
        !user.isAlreadyMember && 
        user.email && 
        !currentMemberEmails.includes(user.email)
      ) || []

      setSlackUsers(availableUsers)
    } catch (err: any) {
      console.error('Error loading Slack users:', err)
      setError(err.message || 'Failed to load Slack users')
    } finally {
      setLoadingSlackUsers(false)
    }
  }

  const loadTeamData = async () => {
    try {
      setLoading(true)
      
      // Get current session for edge function call
      const session = await getCurrentSession()
      if (!session) {
        throw new Error('No session found')
      }

      // Fetch all team users including Slack-invited ones
      const { data: allUsersData, error: usersError } = await supabase.functions.invoke('get-all-team-users', {
        body: { team_id: teamId },
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      })

      if (usersError) throw usersError

      const [invitationsData, teamData] = await Promise.all([
        isAdmin ? getTeamInvitations(teamId) : Promise.resolve([]),
        supabase.from('teams').select('plan').eq('id', teamId).single()
      ])
      
      // Transform the data to match the expected format
      const transformedMembers = allUsersData?.users?.map((user: any) => ({
        ...user,
        users: {
          name: user.user_name,
          email: user.user_email
        }
      })) || []
      
      setMembers(transformedMembers)
      setInvitations(invitationsData || [])
      setTeamPlan(teamData.data?.plan || 'trial')
    } catch (err: any) {
      console.error('Error loading team data:', err)
      setError(err.message || 'Failed to load team data')
    } finally {
      setLoading(false)
    }
  }

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (selectedSlackUsers.size === 0) return

    // Check team member limits first
    const totalMembers = members.length + invitations.length
    const planFeatures = getPlanFeatures(teamPlan)
    const maxUsers = typeof planFeatures.maxUsers === 'number' ? planFeatures.maxUsers : Infinity
    const usersToAdd = selectedSlackUsers.size

    if (totalMembers + usersToAdd > maxUsers) {
      setShowUpgradePrompt(true)
      return
    }

    setInviteLoading(true)
    setError('')

    try {
      // Invite Slack users
      const session = await getCurrentSession()
      if (!session) throw new Error('No session found')

      const usersToInvite = slackUsers
        .filter(user => selectedSlackUsers.has(user.id))
        .map(user => ({
          slack_user_id: user.id,
          name: user.name,
          email: user.email
        }))

      const { error } = await supabase.functions.invoke('invite-team-members', {
        body: { 
          team_id: teamId,
          users: usersToInvite
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      })

      if (error) throw error
      
      await loadTeamData()
      setSelectedSlackUsers(new Set())
      setShowInviteForm(false)
    } catch (err: any) {
      setError(err.message || 'Failed to send invitation')
    } finally {
      setInviteLoading(false)
    }
  }

  const handleRoleChange = async (memberId: string, newRole: TeamRole) => {
    console.log('handleRoleChange: Starting role change for member:', memberId, 'to role:', newRole)
    try {
      await updateTeamMemberRole(memberId, newRole)
      console.log('handleRoleChange: Role update successful, reloading data')
      await loadTeamData()
      await refreshUserTeams()
      console.log('handleRoleChange: Data reload complete')
    } catch (err: any) {
      console.error('handleRoleChange: Error updating role:', err)
      setError(err.message || 'Failed to update role')
    }
  }

  const handleRemoveMember = async (member: any) => {
    if (!confirm(`Are you sure you want to remove ${member.users?.name || 'this member'} from the team?`)) return

    try {
      const session = await getCurrentSession()
      if (!session) throw new Error('No session found')

      // Extract the actual member ID if it's prefixed
      let memberRecordId = null
      if (member.id.startsWith('member_')) {
        memberRecordId = member.id
      } else if (member.id.startsWith('user_')) {
        // This is a Slack-only user, no team_members record
        memberRecordId = null
      } else {
        // Legacy format
        memberRecordId = `member_${member.id}`
      }

      const { error } = await supabase.functions.invoke('remove-team-member', {
        body: {
          teamId,
          userId: member.user_id,
          memberRecordId
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      })

      if (error) throw error

      await loadTeamData()
      await refreshUserTeams()
    } catch (err: any) {
      setError(err.message || 'Failed to remove member')
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="text-sm text-red-700">{error}</div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('members')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'members'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Team Members
          </button>
          <button
            onClick={() => setActiveTab('standup')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'standup'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Standup Config
          </button>
          <button
            onClick={() => setActiveTab('billing')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'billing'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Billing & Plan
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'standup' && (
        <StandupConfiguration teamId={teamId} userRole={userRole} />
      )}

      {activeTab === 'members' && (
        <>
          {/* Team Members */}
          <div>
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Team Members</h3>
            {(() => {
              const totalMembers = members.length + invitations.length
              const planFeatures = getPlanFeatures(teamPlan)
              const maxUsers = typeof planFeatures.maxUsers === 'number' ? planFeatures.maxUsers : '∞'
              const isNearLimit = typeof planFeatures.maxUsers === 'number' && totalMembers >= planFeatures.maxUsers - 1
              const isAtLimit = typeof planFeatures.maxUsers === 'number' && totalMembers >= planFeatures.maxUsers
              
              return (
                <p className={`text-sm ${isAtLimit ? 'text-red-600' : isNearLimit ? 'text-orange-600' : 'text-gray-500'}`}>
                  {totalMembers} of {maxUsers} team members used
                  {isAtLimit && ' (limit reached)'}
                </p>
              )
            })()}
          </div>
          {isAdmin && (
            <button
              onClick={() => {
                setShowInviteForm(true)
                loadSlackUsers()
              }}
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md text-sm font-medium inline-flex items-center"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Invite Member
            </button>
          )}
        </div>

        <div className="bg-white shadow rounded-lg overflow-hidden">
          <ul className="divide-y divide-gray-200">
            {members.map((member) => (
              <li key={member.id} className="px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-10 w-10">
                      <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center">
                        <span className="text-sm font-medium text-indigo-700">
                          {(() => {
                            const name = member.users?.name || member.users?.email || 'UN'
                            return name.split(' ').map((word: string) => word[0]).join('').slice(0, 2).toUpperCase()
                          })()}
                        </span>
                      </div>
                    </div>
                    <div className="ml-4">
                      <div className="flex items-center">
                        <div className="text-sm font-medium text-gray-900">
                          {member.users?.name || member.users?.email || 'Unknown User'}
                        </div>
                        {member.is_slack_only && (
                          <div className="ml-2 flex items-center">
                            <Slack className="h-3 w-3 text-gray-400" />
                            <span className="ml-1 text-xs text-gray-500">Slack user</span>
                          </div>
                        )}
                      </div>
                      <div className="text-sm text-gray-500">
                        Joined {new Date(member.joined_at || member.created_at || '').toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    {isAdmin && member.can_manage ? (
                      <select
                        value={member.role}
                        onChange={(e) => handleRoleChange(member.id, e.target.value as TeamRole)}
                        className="text-sm border border-gray-300 rounded-md px-2 py-1"
                      >
                        <option value="member">Member</option>
                        <option value="admin">Admin</option>
                      </select>
                    ) : (
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        member.role === 'admin' 
                          ? 'bg-purple-100 text-purple-800' 
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {member.role}
                      </span>
                    )}
                    
                    {isAdmin && member.user_id && (
                      <button
                        onClick={() => handleRemoveMember(member)}
                        className="text-red-600 hover:text-red-900 text-sm"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Pending Invitations */}
      {isAdmin && invitations.length > 0 && (
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Pending Invitations</h3>
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <ul className="divide-y divide-gray-200">
              {invitations.map((invitation) => (
                <li key={invitation.id} className="px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {invitation.email}
                      </div>
                      <div className="text-sm text-gray-500">
                        Role: <span className="capitalize">{invitation.role}</span> • 
                        Invited {new Date(invitation.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">
                      Pending
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Invite Form Modal */}
      {showInviteForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Invite Team Members from Slack</h3>

            <form onSubmit={handleInvite}>
              <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select team members from your Slack workspace
                  </label>
                  {loadingSlackUsers ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                    </div>
                  ) : slackUsers.length > 0 ? (
                    <div className="space-y-2 max-h-60 overflow-y-auto border border-gray-200 rounded-md p-3">
                      <div className="mb-2 pb-2 border-b">
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={selectedSlackUsers.size === slackUsers.length && slackUsers.length > 0}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedSlackUsers(new Set(slackUsers.map(u => u.id)))
                              } else {
                                setSelectedSlackUsers(new Set())
                              }
                            }}
                            className="mr-2"
                          />
                          <span className="text-sm font-medium">Select all ({slackUsers.length})</span>
                        </label>
                      </div>
                      {slackUsers.map((user) => (
                        <label key={user.id} className="flex items-center p-2 hover:bg-gray-50 rounded cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedSlackUsers.has(user.id)}
                            onChange={(e) => {
                              const newSelected = new Set(selectedSlackUsers)
                              if (e.target.checked) {
                                newSelected.add(user.id)
                              } else {
                                newSelected.delete(user.id)
                              }
                              setSelectedSlackUsers(newSelected)
                            }}
                            className="mr-3"
                          />
                          <div className="flex-1">
                            <div className="text-sm font-medium text-gray-900">{user.name}</div>
                            <div className="text-xs text-gray-500">{user.email || 'No email'}</div>
                          </div>
                        </label>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <p>No available Slack users found</p>
                      <p className="text-xs mt-1">All workspace members may already be added</p>
                    </div>
                  )}
                  {selectedSlackUsers.size > 0 && (
                    <p className="text-sm text-gray-600 mt-2">
                      {selectedSlackUsers.size} user{selectedSlackUsers.size > 1 ? 's' : ''} selected
                    </p>
                  )}
                </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowInviteForm(false)
                    setSelectedSlackUsers(new Set())
                    setError('')
                  }}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={inviteLoading || selectedSlackUsers.size === 0}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
                >
                  {inviteLoading ? 'Inviting...' : `Invite ${selectedSlackUsers.size > 0 ? `${selectedSlackUsers.size} User${selectedSlackUsers.size > 1 ? 's' : ''}` : 'User'}`}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
        </>
      )}

      {/* Billing Tab */}
      {activeTab === 'billing' && (
        <BillingManagement teamId={teamId} userRole={userRole} />
      )}

      {/* Upgrade Prompt Modal */}
      {showUpgradePrompt && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-6 border w-96 shadow-lg rounded-md bg-white">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-orange-100 mb-4">
                <AlertTriangle className="h-6 w-6 text-orange-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Team Member Limit Reached</h3>
              <p className="text-sm text-gray-600 mb-4">
                Your {teamPlan === 'trial' ? 'trial' : 'Essentials'} plan allows up to 4 team members. 
                To add more members, please upgrade to the Advanced plan.
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-4">
                <div className="flex items-center">
                  <Crown className="h-5 w-5 text-blue-600 mr-2" />
                  <div className="text-left">
                    <p className="text-sm font-medium text-blue-900">Advanced Plan</p>
                    <p className="text-xs text-blue-700">$2.00/user/month • Unlimited team members</p>
                  </div>
                </div>
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowUpgradePrompt(false)}
                  className="flex-1 px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setShowUpgradePrompt(false)
                    setActiveTab('billing')
                  }}
                  className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                >
                  Upgrade Now
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
} 